import type { MediaAssetDto, MediaCollectionDto } from '@reelplicate/api-client/model';

import type { Platform } from '@/features/calendar/platforms';

import { describeRule, type Facet, formatAgo } from './library';
import type {
  Asset,
  AssetKind,
  AssetSource,
  AssetStatus,
  Collection,
  Owner,
  SmartRule,
} from './types';

const DAY_MS = 86_400_000;
const PLATFORMS: Platform[] = ['tiktok', 'instagram', 'youtube'];

/** The Account filter's value for the brand itself (personas use their id). */
export const BRAND_OWNER = 'brand';

/** "18 MB", "1.2 GB" */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** "Added today · 1080×1920 · 18 MB" */
function specs(dto: MediaAssetDto, addedDaysAgo: number) {
  return [
    `Added ${addedDaysAgo === 0 ? 'today' : `${addedDaysAgo}d ago`}`,
    dto.width && dto.height && `${dto.width}×${dto.height}`,
    dto.sizeBytes > 0 && formatBytes(dto.sizeBytes),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** An API asset in the shape the library's components use. */
export function toAsset(dto: MediaAssetDto, owners: Owner[], now = Date.now()): Asset {
  const ownerId = dto.personaId ?? BRAND_OWNER;
  const addedDaysAgo = Math.max(
    0,
    Math.floor((now - new Date(dto.createdAt).getTime()) / DAY_MS),
  );
  const platforms = dto.platforms
    .map((p) => p.toLowerCase() as Platform)
    .filter((p) => PLATFORMS.includes(p));
  return {
    id: dto.id,
    title: dto.title,
    kind: dto.kind.toLowerCase() as AssetKind,
    source: dto.source.toLowerCase() as AssetSource,
    status: (dto.status?.toLowerCase() as AssetStatus | undefined) ?? undefined,
    slides: dto.slideCount ?? undefined,
    slideUrls: dto.slideUrls.length > 0 ? dto.slideUrls : undefined,
    slideText: dto.slides?.map((s) => ({
      slide: s.slide,
      text: s.text ?? undefined,
      context: s.context ?? undefined,
    })),
    duration: dto.durationSeconds ?? undefined,
    savedFrom: dto.savedFrom ?? undefined,
    sourceUrl: dto.sourceUrl ?? undefined,
    ownerId,
    account: owners.find((o) => o.id === ownerId)?.name ?? '',
    addedDaysAgo,
    thumbnail: dto.thumbnailUrl ?? undefined,
    views: dto.views ?? undefined,
    platforms: platforms.length > 0 ? platforms : undefined,
    caption: dto.caption ?? undefined,
    sourceAssetId: dto.sourceAssetId ?? undefined,
    sourceSlide: dto.sourceSlide ?? undefined,
    sourceScene: dto.sourceScene ?? undefined,
    videoUrl: dto.videoUrl ?? undefined,
    scenes: dto.scenes?.map((s) => ({
      scene: s.scene,
      start: s.start,
      end: s.end,
      text: s.text ?? undefined,
      context: s.context ?? undefined,
      speech: s.speech ?? undefined,
      frameUrl: s.frameUrl ?? undefined,
    })),
    tags: dto.tags,
    transcript: dto.transcript ?? undefined,
    fileUrl: dto.fileUrl ?? undefined,
    generation: dto.generation ?? undefined,
    details: {
      avgWatch: dto.avgWatchSeconds !== null ? `${dto.avgWatchSeconds}s` : undefined,
      engagement:
        dto.engagementRate !== null
          ? `${(dto.engagementRate * 100).toFixed(1)}%`
          : undefined,
      specs: specs(dto, addedDaysAgo),
    },
  };
}

/** An API collection; a smart one gets its matcher and readable rule. */
export function toCollection(
  dto: MediaCollectionDto,
  facets: Facet[],
  now = Date.now(),
): Collection {
  const updatedDaysAgo = Math.max(
    0,
    Math.floor((now - new Date(dto.updatedAt).getTime()) / DAY_MS),
  );
  const base = {
    id: dto.id,
    name: dto.name,
    count: dto.count,
    coverIds: dto.coverAssetIds,
    description: dto.description ?? undefined,
    updated: `updated ${formatAgo(updatedDaysAgo)}${updatedDaysAgo === 0 ? '' : ' ago'}`,
  };
  if (!dto.rule) return { ...base, assetIds: dto.assetIds };
  const rule: SmartRule = {
    filters: Object.fromEntries(
      Object.entries(dto.rule.filters).filter((entry): entry is [string, string[]] =>
        Array.isArray(entry[1]),
      ),
    ),
    query: dto.rule.query,
    sort: dto.rule.sort,
    limit: dto.rule.limit ?? null,
    includeCompetitorFeed: dto.rule.includeCompetitorFeed ?? false,
  };
  return {
    ...base,
    smart: { ...rule, rule: describeRule(rule, facets), broken: dto.broken },
  };
}
